/**
 * The four states every page needs: loading, empty, error, and not-built-yet.
 *
 * Kept together in one file because they are a set — a page that has a loading
 * skeleton but no empty state is a page that renders a blank rectangle for the
 * Study Hall course, and that reads as a bug rather than as "nothing here".
 */
import { type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ConstructionIcon from '@mui/icons-material/Construction';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InboxIcon from '@mui/icons-material/Inbox';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import KeyIcon from '@mui/icons-material/Key';

import { CanvasHttpError, CanvasNetworkError, CanvasNoTokenError } from '@/lib/canvas/errors';
import { tokens } from '@/theme/tokens';

/* ------------------------------------------------------------------ */
/* Empty                                                               */
/* ------------------------------------------------------------------ */

export interface EmptyStateProps {
  title: string;
  description?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
}

export function EmptyState({ title, description, icon, action }: EmptyStateProps) {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={2}
      sx={{ py: 8, px: 3, textAlign: 'center', color: 'text.secondary' }}
    >
      <Box sx={{ '& svg': { fontSize: tokens.size.iconLg * 1.5, opacity: tokens.opacity.disabled } }}>
        {icon ?? <InboxIcon />}
      </Box>
      <Typography variant="h6" color="text.primary">
        {title}
      </Typography>
      {description ? (
        <Typography variant="body2" sx={{ maxWidth: tokens.size.narrowMaxWidth / 2 }}>
          {description}
        </Typography>
      ) : null}
      {action}
    </Stack>
  );
}

/* ------------------------------------------------------------------ */
/* Error                                                               */
/* ------------------------------------------------------------------ */

export interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
  /** What the user was trying to see, e.g. "assignments". */
  what?: string;
}

/**
 * Turn a thrown value into something a student can act on.
 *
 * Generic "something went wrong" is close to useless here: "your token expired"
 * and "the school wifi dropped" need completely different responses from the
 * person reading it.
 */
export function ErrorState({ error, onRetry, what = 'this' }: ErrorStateProps) {
  let title = `Could not load ${what}`;
  let detail: ReactNode = error instanceof Error ? error.message : String(error);
  let icon: ReactNode = <ErrorOutlineIcon />;
  let severity: 'error' | 'warning' = 'error';

  if (error instanceof CanvasNoTokenError) {
    title = 'No Canvas token yet';
    detail = 'Add your Canvas access token in Settings to load your courses.';
    icon = <KeyIcon />;
    severity = 'warning';
  } else if (error instanceof CanvasNetworkError) {
    title = 'Could not reach Canvas';
    detail =
      'Check your connection. If you are on school wifi, the proxy domain may be blocked ' +
      'by the device policy — see DEPLOY.md.';
    icon = <WifiOffIcon />;
    severity = 'warning';
  } else if (error instanceof CanvasHttpError) {
    if (error.isAuthError) {
      title = 'Canvas rejected your token';
      detail = 'It may have been revoked or expired. Generate a new one and paste it in Settings.';
      icon = <KeyIcon />;
    } else if (error.isPermissionError) {
      title = 'No access to this';
      detail = 'Canvas says you do not have permission. This can happen on concluded courses.';
      severity = 'warning';
    } else if (error.isNotFound) {
      title = 'Not found';
      detail = 'Canvas could not find that. It may have been deleted or unpublished.';
      severity = 'warning';
    }
  }

  return (
    <Alert
      severity={severity}
      icon={icon}
      sx={{ my: 2 }}
      action={
        onRetry ? (
          <Button color="inherit" size="small" onClick={onRetry}>
            Retry
          </Button>
        ) : undefined
      }
    >
      <AlertTitle>{title}</AlertTitle>
      {detail}
    </Alert>
  );
}

/* ------------------------------------------------------------------ */
/* Loading                                                             */
/* ------------------------------------------------------------------ */

/** A stack of text lines. The default skeleton for list-shaped content. */
export function LoadingLines({ lines = 3, height = 20 }: { lines?: number; height?: number }) {
  return (
    <Stack spacing={1} sx={{ py: 1 }}>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton
          key={i}
          variant="rounded"
          height={height}
          width={`${100 - (i % 3) * 12}%`}
          aria-hidden
        />
      ))}
    </Stack>
  );
}

/** Card-shaped skeleton, sized to match a real course card so nothing jumps. */
export function LoadingCards({ count = 6 }: { count?: number }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 3,
        gridTemplateColumns: {
          // minmax(0, …) to match CourseGrid exactly — see the note there. If
          // these two ever diverge the layout jumps when skeletons are replaced
          // by real cards, which is the thing this component exists to prevent.
          xs: 'minmax(0, 1fr)',
          sm: 'repeat(2, minmax(0, 1fr))',
          lg: 'repeat(3, minmax(0, 1fr))',
        },
      }}
    >
      {Array.from({ length: count }, (_, i) => (
        // Height is pinned to the real card's minimum so the skeleton -> content
        // swap does not reflow the grid under the reader's cursor.
        <Card
          key={i}
          sx={{ overflow: 'hidden', minHeight: tokens.size.courseCardMinHeight }}
          aria-hidden
        >
          <Skeleton variant="rectangular" height={tokens.size.courseCardHeaderHeight} />
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Skeleton width="70%" height={tokens.fontSize.xl} />
            <Skeleton width="40%" height={tokens.fontSize.md} />
            <Skeleton width="55%" height={tokens.fontSize.md} />
            <Skeleton width="45%" height={tokens.fontSize.md} sx={{ mt: 'auto' }} />
          </Box>
        </Card>
      ))}
    </Box>
  );
}

/** Row-shaped skeleton for tables and lists. */
export function LoadingRows({ count = 8 }: { count?: number }) {
  return (
    <Stack spacing={1.5} sx={{ py: 1 }}>
      {Array.from({ length: count }, (_, i) => (
        <Stack key={i} direction="row" spacing={2} alignItems="center" aria-hidden>
          <Skeleton variant="circular" width={tokens.size.avatarLg} height={tokens.size.avatarLg} />
          <Box sx={{ flex: 1 }}>
            <Skeleton width={`${55 + (i % 4) * 10}%`} height={18} />
            <Skeleton width="30%" height={14} />
          </Box>
        </Stack>
      ))}
    </Stack>
  );
}

/* ------------------------------------------------------------------ */
/* Not built yet                                                       */
/* ------------------------------------------------------------------ */

/**
 * Honest placeholder for a route that exists and routes correctly but has no
 * real implementation.
 *
 * BUILD_PROMPT operating rule #5: all pages existing at 80% beats four pages at
 * 100%, and a stub with real routing and a clear "not built yet" beats a route
 * that 404s or silently renders nothing.
 */
export function NotBuiltYet({ page, note }: { page: string; note?: ReactNode }) {
  return (
    <EmptyState
      icon={<ConstructionIcon />}
      title={`${page} is not built yet`}
      description={
        note ?? (
          <>
            The route, layout, and navigation for this page are real — the content is not
            implemented. See <code>PROGRESS.md</code> for what landed and what did not.
          </>
        )
      }
    />
  );
}
